import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';

/** Reactive `matchMedia` — SSR-safe, used by DataTable's mobile stack. */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (cb: () => void) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener('change', cb);
      return () => mql.removeEventListener('change', cb);
    },
    [query],
  );
  const getSnapshot = () =>
    typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false;
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

/** Controlled/uncontrolled state helper (used by inputs, switch, tabs…). */
export function useControllableState<T>(
  controlled: T | undefined,
  defaultValue: T,
  onChange?: (value: T) => void,
): [T, (value: T) => void] {
  const [uncontrolled, setUncontrolled] = useState<T>(defaultValue);
  const isControlled = controlled !== undefined;
  const value = isControlled ? (controlled as T) : uncontrolled;
  const setValue = useCallback(
    (next: T) => {
      if (!isControlled) setUncontrolled(next);
      onChange?.(next);
    },
    [isControlled, onChange],
  );
  return [value, setValue];
}

/** Call `handler` when Escape is pressed while `enabled`. */
export function useOnEscape(handler: () => void, enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handler();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [handler, enabled]);
}

/**
 * Lock page scroll while an overlay is open (nest-safe via a counter).
 *
 * The lock goes on `<html>`, not `<body>`. `body { overflow: hidden }` only
 * stops the page when the browser propagates it to the viewport, and it skips
 * that whenever `<html>` has an overflow of its own. The portal sets
 * `html { overflow-x: clip }`, so a body-only lock did nothing: a swipe or
 * wheel over a short dialog (nothing in it to scroll) scrolled the page behind.
 *
 * Hiding the root's scrollbar would shift the page sideways on screens with
 * classic scrollbars, so its gutter is kept while the lock holds.
 */
let lockCount = 0;
let prevHtmlOverflow = '';
let prevBodyOverflow = '';
let prevGutter = '';
export function useLockBodyScroll(enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    const root = document.documentElement;
    if (lockCount === 0) {
      prevHtmlOverflow = root.style.overflow;
      prevBodyOverflow = document.body.style.overflow;
      prevGutter = root.style.scrollbarGutter;
      // Only a page that scrolls has a scrollbar to keep room for; with overlay
      // scrollbars (phones, macOS) a stable gutter reserves nothing anyway.
      if (root.scrollHeight > root.clientHeight) root.style.scrollbarGutter = 'stable';
      root.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }
    lockCount += 1;
    return () => {
      lockCount -= 1;
      if (lockCount === 0) {
        root.style.overflow = prevHtmlOverflow;
        document.body.style.overflow = prevBodyOverflow;
        root.style.scrollbarGutter = prevGutter;
      }
    };
  }, [enabled]);
}

/** Trap focus inside `ref` and restore it to the previously-focused element
 *  on unmount. Used by Modal / Drawer / ConfirmDialog. */
export function useFocusTrap(
  ref: React.RefObject<HTMLElement>,
  enabled = true,
): void {
  useEffect(() => {
    if (!enabled || !ref.current) return;
    const node = ref.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const focusables = () =>
      Array.from(
        node.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);

    // Move focus into the dialog.
    const first = focusables()[0] ?? node;
    first.focus?.();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const firstEl = items[0]!;
      const lastEl = items[items.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && active === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && active === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };

    node.addEventListener('keydown', onKeyDown);
    return () => {
      node.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [ref, enabled]);
}

/** Fire `handler` on a pointer press or focus move outside `ref`. */
export function useClickOutside(
  ref: React.RefObject<HTMLElement>,
  handler: () => void,
  enabled = true,
): void {
  const saved = useRef(handler);
  saved.current = handler;
  useEffect(() => {
    if (!enabled) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      const el = ref.current;
      if (el && !el.contains(e.target as Node)) saved.current();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [ref, enabled]);
}
